import { Component, ElementRef, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild } from '@angular/core';
import { Router } from '@angular/router';

export interface VwCommandItem {
  name: string;
  url: string;
  icon?: string;
  group?: string;
}

// Ctrl/Cmd+K — jump to any page this user actually has a sidebar link for.
// Deliberately reuses the SAME navItems data the sidebar already renders
// (flattened), rather than a separately hardcoded list — that's what
// guarantees this never offers a destination the user doesn't have rights
// to, with zero extra permission logic to get wrong.
@Component({
  selector: 'app-command-palette',
  templateUrl: './command-palette.component.html',
  styleUrls: ['./command-palette.component.scss'],
})
export class CommandPaletteComponent implements OnChanges {
  @Input() open = false;
  @Input() items: VwCommandItem[] = [];
  // Actual page visits, separate from search — shown first when the
  // palette opens with nothing typed yet, so the handful of pages someone
  // actually works in all day are one click away instead of buried in the
  // full nav list.
  @Input() recentItems: VwCommandItem[] = [];
  @Output() close = new EventEmitter<void>();
  @ViewChild('searchInput') searchInputRef: ElementRef<HTMLInputElement>;

  query = '';
  activeIndex = 0;

  constructor(private router: Router) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.open && this.open) {
      this.query = '';
      this.activeIndex = 0;
      // Wait a tick for *ngIf to render the input before focusing it.
      setTimeout(() => this.searchInputRef && this.searchInputRef.nativeElement.focus(), 0);
    }
  }

  get filtered(): VwCommandItem[] {
    const q = this.query.trim().toLowerCase();
    if (!q) {
      const base = this.recentItems.length ? this.recentItems : this.items;
      return base.slice(0, 40);
    }
    return this.items.filter(i => i.name.toLowerCase().includes(q)).slice(0, 40);
  }

  get isShowingRecent(): boolean {
    return this.query.trim().length === 0 && this.recentItems.length > 0;
  }

  onKeydown(event: KeyboardEvent): void {
    const results = this.filtered;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.activeIndex = Math.min(this.activeIndex + 1, results.length - 1);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.activeIndex = Math.max(this.activeIndex - 1, 0);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const item = results[this.activeIndex];
      if (item) {
        this.go(item);
      }
    } else if (event.key === 'Escape') {
      this.dismiss();
    }
  }

  onQueryChange(): void {
    this.activeIndex = 0;
  }

  go(item: VwCommandItem): void {
    this.router.navigateByUrl(item.url);
    this.dismiss();
  }

  dismiss(): void {
    this.close.emit();
  }
}
